import { useEffect, useState } from "react";
import { Clock3, Loader2 } from "lucide-react";
import { api, getTrialId } from "@/lib/api";

export function ProcessingEstimate({ file, clipCount, maxSeconds, faceTrack, captions, userId, onEstimate }) {
  const [metadata, setMetadata] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    const settle = (value) => {
      clearTimeout(timer);
      if (!cancelled) setMetadata({ file, ...value });
    };
    const timer = setTimeout(() => settle({ error: "Video length could not be read in this browser." }), 15000);
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const seconds = video.duration;
      settle(Number.isFinite(seconds) && seconds > 0 && seconds <= 86400
        ? { seconds } : { error: "A reliable video length is unavailable." });
    };
    video.onerror = () => settle({ error: "Video length could not be read in this browser." });
    video.src = url;
    return () => {
      cancelled = true;
      clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const current = metadata?.file === file ? metadata : null;
  const validCount = Number.isInteger(Number(clipCount)) && Number(clipCount) >= 1 && Number(clipCount) <= 40;
  const key = `${current?.seconds}:${clipCount}:${maxSeconds}:${faceTrack}:${captions}:${userId || "trial"}`;
  useEffect(() => {
    if (!current?.seconds || !validCount) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api.post("/processing/estimate", {
        duration_seconds: current.seconds, clips_requested: Number(clipCount),
        max_clip_seconds: Number(maxSeconds), face_track: faceTrack, captions, trial_id: getTrialId(),
      }, { signal: controller.signal }).then(({ data }) => {
        if (!controller.signal.aborted) setResult({ key, ...data });
      }).catch(() => {
        if (!controller.signal.aborted) setResult({ key, error: "Estimate temporarily unavailable. You can still upload." });
      });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [current, validCount, key, clipCount, maxSeconds, faceTrack, captions]);

  const estimate = result?.key === key ? result : null;
  const error = current?.error || (!validCount ? "Enter a count of 1–40 clips to see an estimate." : estimate?.error);
  const pending = !error && (!current || !estimate);
  useEffect(() => {
    onEstimate?.({ ...estimate, file, clipCount: Number(clipCount), maxSeconds, faceTrack, captions, userId,
      pending, duration_seconds: current?.seconds,
      feasible_clips: estimate?.feasible_clips ?? (current?.seconds ? Math.max(1, Math.min(40, Math.floor(current.seconds / 30))) : null),
    });
  }, [onEstimate, estimate, file, clipCount, maxSeconds, faceTrack, captions, userId, pending, current]);

  if (!file) return null;
  const seconds = Math.round(current?.seconds || 0);
  return (
    <section data-testid="processing-estimate" aria-live="polite" className="rounded-2xl border border-cyan-500/20 bg-surface-950 p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-copy-200">
        <Clock3 size={16} className="text-cyan-400" /> Approximate processing time
      </div>
      {pending ? <p data-testid="estimate-loading" className="mt-2 flex items-center gap-2 text-sm text-copy-400"><Loader2 size={14} className="animate-spin" />{current ? "Calculating estimate…" : "Reading video length…"}</p>
        : error ? <p data-testid="estimate-unavailable" className="mt-2 text-sm text-copy-400">{error} {current?.error && "You can still upload the file."}</p>
        : !estimate.available ? <p data-testid="estimate-allowance-message" className="mt-2 text-sm text-amber-300">{estimate.reason}</p>
        : <>
          <p data-testid="estimate-range" className="mt-2 font-display text-2xl font-bold text-cyan-400">{estimate.lower_minutes}–{estimate.upper_minutes} min</p>
          <p data-testid="estimate-basis" className="mt-1 text-xs text-copy-400">{Math.floor(seconds / 60)}m {seconds % 60}s video · processing budget for {estimate.clips_budgeted} clips within your allowance</p>
          <p data-testid="estimate-feasibility" className="mt-2 text-xs text-copy-400">Suggested count: {estimate.recommended_clips} · roughly one clip per 30 seconds of source, not a guarantee of quality.</p>
          {Number(clipCount) > estimate.clips_budgeted && <p data-testid="estimate-quota-cap" className="mt-2 text-xs text-amber-300">Your allowance limits this upload to {estimate.clips_budgeted} clips, even if you continue with a higher count.</p>}
          {seconds < 12 && <p data-testid="estimate-too-short" className="mt-2 text-xs text-amber-300">Choose a video at least 12 seconds long to create a clip.</p>}
        </>}
      <p data-testid="estimate-disclaimer" className="mt-3 text-xs leading-relaxed text-copy-500">A planning estimate, not a guarantee. Upload time is separate. Fewer or shorter clips may finish sooner; server load and video complexity can take longer. This is not a hosting-tier benchmark.</p>
    </section>
  );
}
